import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { addEnteringLeavingAirStatus } from './excelExportEnteringLeavingAir';
import { addFanSelection } from './excelExportFanSelection';

// 스타일 정의
const STYLES = {
  TITLE: {
    font: { name: '맑은 고딕', size: 18, bold: true, color: { argb: 'FF1976d2' } },
    alignment: { vertical: 'middle', horizontal: 'left' }
  } as Partial<ExcelJS.Style>,

  SUBTITLE: {
    font: { name: '맑은 고딕', size: 14, bold: true, color: { argb: 'FF1976d2' } },
    alignment: { vertical: 'middle', horizontal: 'left' }
  } as Partial<ExcelJS.Style>,

  SECTION_HEADER: {
    font: { name: '맑은 고딕', size: 11, bold: true, color: { argb: 'FFFFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } } as ExcelJS.FillPattern,
    alignment: { vertical: 'middle', horizontal: 'left' }
  } as Partial<ExcelJS.Style>,

  TABLE_CELL: {
    font: { name: '맑은 고딕', size: 10 },
    alignment: { vertical: 'middle', horizontal: 'left' },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  } as Partial<ExcelJS.Style>,

  TABLE_CELL_CENTER: {
    font: { name: '맑은 고딕', size: 10 },
    alignment: { vertical: 'middle', horizontal: 'center' },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  } as Partial<ExcelJS.Style>,

  TABLE_CELL_RIGHT: {
    font: { name: '맑은 고딕', size: 10 },
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  } as Partial<ExcelJS.Style>,

  TABLE_CELL_BOLD: {
    font: { name: '맑은 고딕', size: 10, bold: true },
    alignment: { vertical: 'middle', horizontal: 'left' },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  } as Partial<ExcelJS.Style>,

  TABLE_CELL_HIGHLIGHT: {
    font: { name: '맑은 고딕', size: 10, bold: true },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF9E5' } } as ExcelJS.FillPattern,
    alignment: { vertical: 'middle', horizontal: 'center' },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  } as Partial<ExcelJS.Style>
};

/**
 * Base64 문자열을 ArrayBuffer로 변환
 */
const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * 상태점 표시 이름 변환 (습공기선도의 라벨과 동일)
 */
const getStatePointDisplayName = (point: any): string => {
  const pointName = (point.name || '').toLowerCase();

  // 습공기선도의 getFormattedLabelAndSymbol과 동일한 로직 사용
  if (pointName === 'r1') {
    return 'r1(환기 온도상승 후)';
  } else if (pointName.includes('ra') || (point.name.includes('환기') && point.name.includes('RA'))) {
    return 'ra(환기)';
  } else if (pointName === 'r2') {
    return 'r2(난방 환기)';
  } else if (pointName.includes('oa') || (point.name.includes('외기') && point.name.includes('OA'))) {
    return 'oa(외기)';
  } else if (pointName === 'o2') {
    return 'o2(난방 외기)';
  } else if (pointName.includes('ma') || (point.name.includes('혼합') && point.name.includes('MA'))) {
    return 'ma(혼합)';
  } else if (pointName === 'm1') {
    return 'm1(난방 혼합)';
  } else if (pointName.includes('l1') || (point.name.includes('냉각후') && point.name.includes('L1'))) {
    return 'l1(냉각후)';
  } else if (pointName === 'l2') {
    return 'l2(난방 급기)';
  } else if (pointName.includes('la') && !pointName.includes('sa')) {
    return 'la(Leave Air)';
  } else if (pointName === 'l3') {
    return 'l3(난방 가열후)';
  } else if (pointName.includes('sa') || (point.name.includes('급기') && point.name.includes('SA'))) {
    return 'sa(급기)';
  } else if (pointName.includes('adp')) {
    return 'adp';
  }

  return point.name.toLowerCase() || '-';
};

/**
 * Excel 셀 값을 안전하게 변환 (undefined, NaN, Infinity 등 처리)
 */
const sanitizeCellValue = (value: any): string | number | boolean | null => {
  // null 또는 undefined
  if (value === null || value === undefined) {
    return '';
  }

  // 숫자 타입
  if (typeof value === 'number') {
    if (isNaN(value) || !isFinite(value)) {
      return '';
    }
    return value;
  }

  // 문자열 타입
  if (typeof value === 'string') {
    return value;
  }

  // 불리언 타입
  if (typeof value === 'boolean') {
    return value;
  }

  // 그 외의 경우 문자열로 변환
  try {
    return String(value);
  } catch {
    return '';
  }
};

/**
 * 병합된 셀에 스타일 적용 (경계선 포함)
 */
const applyMergedCellStyle = (
  worksheet: ExcelJS.Worksheet,
  range: string,
  style: Partial<ExcelJS.Style>
): void => {
  // 범위 파싱 (예: "A1:B2")
  const [start, end] = range.split(':');
  if (!end) {
    // 단일 셀
    worksheet.getCell(start).style = style;
    return;
  }

  const startCol = start.match(/[A-Z]+/)?.[0] || '';
  const startRow = parseInt(start.match(/\d+/)?.[0] || '0');
  const endCol = end.match(/[A-Z]+/)?.[0] || '';
  const endRow = parseInt(end.match(/\d+/)?.[0] || '0');

  const colToNum = (col: string): number => {
    let num = 0;
    for (let i = 0; i < col.length; i++) {
      num = num * 26 + (col.charCodeAt(i) - 64);
    }
    return num;
  };

  const numToCol = (num: number): string => {
    let col = '';
    while (num > 0) {
      const rem = (num - 1) % 26;
      col = String.fromCharCode(65 + rem) + col;
      num = Math.floor((num - rem) / 26);
    }
    return col;
  };

  const startColNum = colToNum(startCol);
  const endColNum = colToNum(endCol);

  // 모든 셀에 스타일 적용
  for (let row = startRow; row <= endRow; row++) {
    for (let col = startColNum; col <= endColNum; col++) {
      const cellRef = `${numToCol(col)}${row}`;
      worksheet.getCell(cellRef).style = style;
    }
  }
};

/**
 * 수식 문자열 내의 숫자에 천단위 콤마 추가
 */
const formatFormulaNumbers = (formula: string): string => {
  if (!formula) return formula;

  // 숫자(정수 및 소수)를 찾아서 천단위 콤마 추가
  // 단, 이미 콤마가 있는 숫자는 건너뜀
  return formula.replace(/(?<!\d,)\b(\d{1,3}(?:,\d{3})*|\d+)(?:\.\d+)?\b/g, (match) => {
    // 이미 콤마가 있으면 그대로 반환
    if (match.includes(',')) return match;

    // 소수점이 있는 경우
    if (match.includes('.')) {
      const [intPart, decPart] = match.split('.');
      const formattedInt = parseInt(intPart).toLocaleString();
      return `${formattedInt}.${decPart}`;
    }

    // 정수만 있는 경우
    return parseInt(match).toLocaleString();
  });
};

/**
 * 공조기별 상세 시트 생성
 */
const createAHUDetailSheet = async (
  workbook: ExcelJS.Workbook,
  ahu: any,
  index: number
): Promise<ExcelJS.Worksheet> => {
  const sheetName = ahu.ahuNo || `AHU-${index + 1}`;
  const worksheet = workbook.addWorksheet(sheetName, {
    pageSetup: {
      paperSize: 9, // A4
      orientation: 'landscape',
      margins: { left: 0.5, right: 0.5, top: 0.7, bottom: 0.7, header: 0.3, footer: 0.3 },
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0
    }
  });

  // 열 너비 설정 (13열 구조) - 모든 열 너비 10으로 통일
  worksheet.columns = [
    { width: 10 }, // A - Internal S.P 라벨1
    { width: 10 }, // B - Internal S.P 라벨2
    { width: 10 }, // C - Internal S.P 값
    { width: 10 }, // D - External S.P 라벨1
    { width: 10 }, // E - External S.P 라벨2
    { width: 10 }, // F - External S.P (SA)
    { width: 10 }, // G - External S.P (RA)
    { width: 10 }, // H - Supply/Return Fan 라벨1
    { width: 10 }, // I - Supply/Return Fan 라벨2
    { width: 10 }, // J - Supply/Return Fan 값1
    { width: 10 }, // K - Supply/Return Fan 값2
    { width: 10 }, // L - Supply/Return Fan 값3
    { width: 10 }  // M - Supply/Return Fan 값4
  ];

  const outputData = ahu.outputSheetData;
  if (!outputData) {
    console.error('outputSheetData가 없습니다:', ahu);
    return;
  }

  const { systemInfo, designConditions, loadPercentages, coilCapacities } = outputData;
  const formData = ahu.formData || {};

  let currentRow = 1;

  // === 헤더 ===
  worksheet.mergeCells(`A${currentRow}:N${currentRow}`);
  const titleCell = worksheet.getCell(`A${currentRow}`);
  titleCell.value = 'AH Selection (공조기 선정표)';
  titleCell.style = STYLES.TITLE;
  worksheet.getRow(currentRow).height = 30;
  currentRow++;

  currentRow++; // 빈 행

  // === ■ 실내 부하 / 부하 분석 / 설계 조건 ===
  worksheet.mergeCells(`A${currentRow}:N${currentRow}`);
  const section1Cell = worksheet.getCell(`A${currentRow}`);
  section1Cell.value = '■ 실내 부하 / 부하 분석 / 설계 조건';
  section1Cell.style = STYLES.SECTION_HEADER;
  worksheet.getRow(currentRow).height = 22;
  currentRow++;

  // 1행: AHU 정보 + Zone Block Peak Time
  const zoneBlockPeakTime = formData.zoneBlockPeakTime || '15:00';
  const ahuInfo = `${ahu.ahuNo}: ${formData.location || '설치위치'}  수량: 1`;

  let row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
  row.getCell('A').value = ahuInfo;
  applyMergedCellStyle(worksheet, `A${currentRow}:H${currentRow}`, STYLES.TABLE_CELL);

  worksheet.mergeCells(`I${currentRow}:N${currentRow}`);
  row.getCell('I').value = `Zone Block Peak Time : ${zoneBlockPeakTime}`;
  applyMergedCellStyle(worksheet, `I${currentRow}:N${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });
  currentRow++;

  // 실내냉방부하
  const startLoadRow = currentRow;

  // 2행: 현열
  row = worksheet.getRow(currentRow);
  row.getCell('A').value = '실내냉방부하';
  row.getCell('A').style = { ...STYLES.TABLE_CELL_BOLD, alignment: { ...STYLES.TABLE_CELL_BOLD.alignment, horizontal: 'center' } };

  worksheet.mergeCells(`B${currentRow}:C${currentRow}`);
  row.getCell('B').value = '현열 :';
  applyMergedCellStyle(worksheet, `B${currentRow}:C${currentRow}`, STYLES.TABLE_CELL);

  // D-E열: 값 영역 (오른쪽 경계선 없이 단위와 연결)
  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = systemInfo.sensibleCoolingLoad;
  row.getCell('D').numFmt = '#,##0';
  row.getCell('D').style = {
    ...STYLES.TABLE_CELL,
    numFmt: '#,##0',
    alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' },
    border: {
      top: STYLES.TABLE_CELL.border?.top,
      bottom: STYLES.TABLE_CELL.border?.bottom,
      left: STYLES.TABLE_CELL.border?.left
    }
  };
  // E열도 같은 스타일 적용 (병합된 오른쪽 셀)
  row.getCell('E').style = {
    ...STYLES.TABLE_CELL,
    border: {
      top: STYLES.TABLE_CELL.border?.top,
      bottom: STYLES.TABLE_CELL.border?.bottom
    }
  };

  // F열: 단위 (왼쪽 경계선 없음)
  row.getCell('F').value = 'W';
  row.getCell('F').style = {
    ...STYLES.TABLE_CELL,
    border: {
      top: STYLES.TABLE_CELL.border?.top,
      bottom: STYLES.TABLE_CELL.border?.bottom,
      right: STYLES.TABLE_CELL.border?.right
    }
  };

  worksheet.mergeCells(`G${currentRow}:H${currentRow}`);
  row.getCell('G').value = '장치냉방부하(W/㎡)';
  applyMergedCellStyle(worksheet, `G${currentRow}:H${currentRow}`, STYLES.TABLE_CELL_HIGHLIGHT);

  // 값 검증 후 안전하게 설정 (빈 값은 0으로 표시)
  const deviceCoolingPerArea = parseFloat(systemInfo.deviceCoolingPerArea);
  row.getCell('I').value = isNaN(deviceCoolingPerArea) ? 0 : deviceCoolingPerArea;
  row.getCell('I').numFmt = '0.00';
  row.getCell('I').style = { ...STYLES.TABLE_CELL_HIGHLIGHT, numFmt: '0.00', font: { ...STYLES.TABLE_CELL_HIGHLIGHT.font, bold: false }, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'right' } };

  row.getCell('J').value = '냉방부하(%)';
  row.getCell('J').style = STYLES.TABLE_CELL_HIGHLIGHT;

  worksheet.mergeCells(`K${currentRow}:L${currentRow}`);
  row.getCell('K').value = 'Room Load';
  applyMergedCellStyle(worksheet, `K${currentRow}:L${currentRow}`, STYLES.TABLE_CELL_HIGHLIGHT);

  worksheet.mergeCells(`M${currentRow}:N${currentRow}`);
  // 값 검증 후 안전하게 설정 (빈 값은 0으로 표시)
  const roomLoadPercentage = parseFloat(loadPercentages.cooling.roomLoad);
  row.getCell('M').value = isNaN(roomLoadPercentage) ? 0 : roomLoadPercentage;
  row.getCell('M').numFmt = '0.0';
  applyMergedCellStyle(worksheet, `M${currentRow}:N${currentRow}`, { ...STYLES.TABLE_CELL_HIGHLIGHT, numFmt: '0.0', font: { ...STYLES.TABLE_CELL_HIGHLIGHT.font, bold: false }, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'right' } });
  currentRow++;

  // 3행: 잠열
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`B${currentRow}:C${currentRow}`);
  row.getCell('B').value = '잠열 :';
  applyMergedCellStyle(worksheet, `B${currentRow}:C${currentRow}`, STYLES.TABLE_CELL);

  // D-E열: 값 영역 (오른쪽 경계선 없이 단위와 연결)
  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = systemInfo.latentCoolingLoad;
  row.getCell('D').numFmt = '#,##0';
  row.getCell('D').style = {
    ...STYLES.TABLE_CELL,
    numFmt: '#,##0',
    alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' },
    border: {
      top: STYLES.TABLE_CELL.border?.top,
      bottom: STYLES.TABLE_CELL.border?.bottom,
      left: STYLES.TABLE_CELL.border?.left
    }
  };
  row.getCell('E').style = {
    ...STYLES.TABLE_CELL,
    border: {
      top: STYLES.TABLE_CELL.border?.top,
      bottom: STYLES.TABLE_CELL.border?.bottom
    }
  };

  // F열: 단위 (왼쪽 경계선 없음)
  row.getCell('F').value = 'W';
  row.getCell('F').style = {
    ...STYLES.TABLE_CELL,
    border: {
      top: STYLES.TABLE_CELL.border?.top,
      bottom: STYLES.TABLE_CELL.border?.bottom,
      right: STYLES.TABLE_CELL.border?.right
    }
  };

  worksheet.mergeCells(`G${currentRow}:H${currentRow}`);
  row.getCell('G').value = '장치난방부하(W/㎡)';
  applyMergedCellStyle(worksheet, `G${currentRow}:H${currentRow}`, STYLES.TABLE_CELL_HIGHLIGHT);

  // 값 검증 후 안전하게 설정 (빈 값은 0으로 표시)
  const deviceHeatingPerArea = parseFloat(systemInfo.deviceHeatingPerArea);
  row.getCell('I').value = isNaN(deviceHeatingPerArea) ? 0 : deviceHeatingPerArea;
  row.getCell('I').numFmt = '0.00';
  row.getCell('I').style = { ...STYLES.TABLE_CELL_HIGHLIGHT, numFmt: '0.00', font: { ...STYLES.TABLE_CELL_HIGHLIGHT.font, bold: false }, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'right' } };

  worksheet.mergeCells(`K${currentRow}:L${currentRow}`);
  row.getCell('K').value = 'OA Load';
  applyMergedCellStyle(worksheet, `K${currentRow}:L${currentRow}`, STYLES.TABLE_CELL_HIGHLIGHT);

  worksheet.mergeCells(`M${currentRow}:N${currentRow}`);
  // 값 검증 후 안전하게 설정 (빈 값은 0으로 표시)
  const oaLoadPercentage = parseFloat(loadPercentages.cooling.oaLoad);
  row.getCell('M').value = isNaN(oaLoadPercentage) ? 0 : oaLoadPercentage;
  row.getCell('M').numFmt = '0.0';
  applyMergedCellStyle(worksheet, `M${currentRow}:N${currentRow}`, { ...STYLES.TABLE_CELL_HIGHLIGHT, numFmt: '0.0', font: { ...STYLES.TABLE_CELL_HIGHLIGHT.font, bold: false }, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'right' } });
  currentRow++;

  // 4행: 천장속 부하
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`B${currentRow}:C${currentRow}`);
  row.getCell('B').value = '천장속 부하(Q1) :';
  applyMergedCellStyle(worksheet, `B${currentRow}:C${currentRow}`, STYLES.TABLE_CELL);

  // D-E열: 값 영역 (오른쪽 경계선 없이 단위와 연결)
  const ventilationLightingLoad = parseFloat(formData.ventilationLightingLoad) || 0;
  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = ventilationLightingLoad;
  row.getCell('D').numFmt = '#,##0';
  row.getCell('D').style = {
    ...STYLES.TABLE_CELL,
    numFmt: '#,##0',
    alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' },
    border: {
      top: STYLES.TABLE_CELL.border?.top,
      bottom: STYLES.TABLE_CELL.border?.bottom,
      left: STYLES.TABLE_CELL.border?.left
    }
  };
  row.getCell('E').style = {
    ...STYLES.TABLE_CELL,
    border: {
      top: STYLES.TABLE_CELL.border?.top,
      bottom: STYLES.TABLE_CELL.border?.bottom
    }
  };

  // F열: 단위 (왼쪽 경계선 없음)
  row.getCell('F').value = 'W';
  row.getCell('F').style = {
    ...STYLES.TABLE_CELL,
    border: {
      top: STYLES.TABLE_CELL.border?.top,
      bottom: STYLES.TABLE_CELL.border?.bottom,
      right: STYLES.TABLE_CELL.border?.right
    }
  };

  worksheet.mergeCells(`G${currentRow}:H${currentRow}`);
  row.getCell('G').value = '급기 풍량(㎥/h/㎡)';
  applyMergedCellStyle(worksheet, `G${currentRow}:H${currentRow}`, STYLES.TABLE_CELL_HIGHLIGHT);

  // 값 검증 후 안전하게 설정
  const supplyAirPerArea = parseFloat(systemInfo.supplyAirPerArea);
  row.getCell('I').value = sanitizeCellValue(isNaN(supplyAirPerArea) ? '' : supplyAirPerArea);
  row.getCell('I').numFmt = '0.00';
  row.getCell('I').style = { ...STYLES.TABLE_CELL_HIGHLIGHT, numFmt: '0.00', font: { ...STYLES.TABLE_CELL_HIGHLIGHT.font, bold: false }, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'right' } };

  worksheet.mergeCells(`K${currentRow}:L${currentRow}`);
  row.getCell('K').value = 'Add & Fan';
  applyMergedCellStyle(worksheet, `K${currentRow}:L${currentRow}`, STYLES.TABLE_CELL_HIGHLIGHT);

  worksheet.mergeCells(`M${currentRow}:N${currentRow}`);
  // 값 검증 후 안전하게 설정
  const addFanPercentage = parseFloat(loadPercentages.cooling.addFan);
  row.getCell('M').value = sanitizeCellValue(isNaN(addFanPercentage) ? '' : addFanPercentage);
  row.getCell('M').numFmt = '0.0';
  applyMergedCellStyle(worksheet, `M${currentRow}:N${currentRow}`, { ...STYLES.TABLE_CELL_HIGHLIGHT, numFmt: '0.0', font: { ...STYLES.TABLE_CELL_HIGHLIGHT.font, bold: false }, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'right' } });

  // A열 병합 (실내냉방부하)
  worksheet.mergeCells(`A${startLoadRow}:A${currentRow}`);
  // J열 병합 (냉방부하%)
  worksheet.mergeCells(`J${startLoadRow}:J${currentRow}`);
  currentRow++;

  // 5행: 실내난방부하
  const heatingLoad = parseFloat(formData.heatingLoad) || 0;
  row = worksheet.getRow(currentRow);
  row.getCell('A').value = '실내난방부하';
  row.getCell('A').style = { ...STYLES.TABLE_CELL_BOLD, alignment: { ...STYLES.TABLE_CELL_BOLD.alignment, horizontal: 'center' } };

  worksheet.mergeCells(`B${currentRow}:C${currentRow}`);
  row.getCell('B').value = '전열 :';
  applyMergedCellStyle(worksheet, `B${currentRow}:C${currentRow}`, STYLES.TABLE_CELL);

  // D-E열: 값 영역 (오른쪽 경계선 없이 단위와 연결)
  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = heatingLoad;
  row.getCell('D').numFmt = '#,##0';
  row.getCell('D').style = {
    ...STYLES.TABLE_CELL,
    numFmt: '#,##0',
    alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' },
    border: {
      top: STYLES.TABLE_CELL.border?.top,
      bottom: STYLES.TABLE_CELL.border?.bottom,
      left: STYLES.TABLE_CELL.border?.left
    }
  };
  row.getCell('E').style = {
    ...STYLES.TABLE_CELL,
    border: {
      top: STYLES.TABLE_CELL.border?.top,
      bottom: STYLES.TABLE_CELL.border?.bottom
    }
  };

  // F열: 단위 (왼쪽 경계선 없음)
  row.getCell('F').value = 'W';
  row.getCell('F').style = {
    ...STYLES.TABLE_CELL,
    border: {
      top: STYLES.TABLE_CELL.border?.top,
      bottom: STYLES.TABLE_CELL.border?.bottom,
      right: STYLES.TABLE_CELL.border?.right
    }
  };

  worksheet.mergeCells(`G${currentRow}:H${currentRow}`);
  row.getCell('G').value = '환기 횟수(a.c/h)';
  applyMergedCellStyle(worksheet, `G${currentRow}:H${currentRow}`, STYLES.TABLE_CELL_HIGHLIGHT);

  // 값 검증 후 안전하게 설정
  const airChange = parseFloat(systemInfo.airChange);
  row.getCell('I').value = sanitizeCellValue(isNaN(airChange) ? '' : airChange);
  row.getCell('I').numFmt = '0.00';
  row.getCell('I').style = { ...STYLES.TABLE_CELL_HIGHLIGHT, numFmt: '0.00', font: { ...STYLES.TABLE_CELL_HIGHLIGHT.font, bold: false }, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'right' } };

  row.getCell('J').value = '난방부하(%)';
  row.getCell('J').style = { ...STYLES.TABLE_CELL_HIGHLIGHT };

  worksheet.mergeCells(`K${currentRow}:L${currentRow}`);
  row.getCell('K').value = 'Room Load';
  applyMergedCellStyle(worksheet, `K${currentRow}:L${currentRow}`, STYLES.TABLE_CELL_HIGHLIGHT);

  worksheet.mergeCells(`M${currentRow}:N${currentRow}`);
  // 값 검증 후 안전하게 설정
  const heatingRoomLoad = parseFloat(loadPercentages.heating.roomLoad || '0');
  row.getCell('M').value = sanitizeCellValue(isNaN(heatingRoomLoad) ? '' : heatingRoomLoad);
  row.getCell('M').numFmt = '0.0';
  applyMergedCellStyle(worksheet, `M${currentRow}:N${currentRow}`, { ...STYLES.TABLE_CELL_HIGHLIGHT, numFmt: '0.0', font: { ...STYLES.TABLE_CELL_HIGHLIGHT.font, bold: false }, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'right' } });
  currentRow++;

  // 6행: 배열회수기 헤더
  row = worksheet.getRow(currentRow);
  row.getCell('A').value = '배열회수기';
  row.getCell('A').style = STYLES.TABLE_CELL_CENTER;

  row.getCell('B').value = '냉방효율';
  row.getCell('B').style = STYLES.TABLE_CELL_CENTER;

  row.getCell('C').value = '난방효율';
  row.getCell('C').style = STYLES.TABLE_CELL_CENTER;

  row.getCell('D').value = '인원수';
  row.getCell('D').style = STYLES.TABLE_CELL_CENTER;

  row.getCell('E').value = '㎥/h/인';
  row.getCell('E').style = STYLES.TABLE_CELL_CENTER;

  row.getCell('F').value = 'RSHF';
  row.getCell('F').style = STYLES.TABLE_CELL_CENTER;

  row.getCell('G').value = '면적(㎡)';
  row.getCell('G').style = STYLES.TABLE_CELL_HIGHLIGHT;

  row.getCell('H').value = '천장고(m)';
  row.getCell('H').style = STYLES.TABLE_CELL_HIGHLIGHT;

  row.getCell('I').value = '체적(㎥)';
  row.getCell('I').style = STYLES.TABLE_CELL_HIGHLIGHT;

  row.getCell('J').value = '난방부하(%)';
  row.getCell('J').style = { ...STYLES.TABLE_CELL_HIGHLIGHT };

  worksheet.mergeCells(`K${currentRow}:L${currentRow}`);
  row.getCell('K').value = 'OA Load';
  applyMergedCellStyle(worksheet, `K${currentRow}:L${currentRow}`, STYLES.TABLE_CELL_HIGHLIGHT);

  worksheet.mergeCells(`M${currentRow}:N${currentRow}`);
  // 값 검증 후 안전하게 설정
  const heatingOaLoad = parseFloat(loadPercentages.heating.oaLoad || '0');
  row.getCell('M').value = sanitizeCellValue(isNaN(heatingOaLoad) ? '' : heatingOaLoad);
  row.getCell('M').numFmt = '0.0';
  applyMergedCellStyle(worksheet, `M${currentRow}:N${currentRow}`, { ...STYLES.TABLE_CELL_HIGHLIGHT, numFmt: '0.0', font: { ...STYLES.TABLE_CELL_HIGHLIGHT.font, bold: false }, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'right' } });
  currentRow++;

  // 7행: 배열회수기 값
  const personnelPerHour = parseFloat(formData.airflowPerPerson || formData.personnelPerHour) || 25;
  const heatRecoveryType = formData.heatRecoveryType || 'None';
  const isHeatRecoveryNone = heatRecoveryType === 'None';

  // 효율 값 파싱 (문자열 또는 숫자 처리)
  const parsedCoolingEff = parseFloat(String(formData.coolingRecoveryEfficiency || '0'));
  const parsedHeatingEff = parseFloat(String(formData.heatingRecoveryEfficiency || '0'));
  const coolingEfficiency = isNaN(parsedCoolingEff) ? 0 : parsedCoolingEff;
  const heatingEfficiency = isNaN(parsedHeatingEff) ? 0 : parsedHeatingEff;

  // console.log('🔍 배열회수기 효율 디버깅:', {
  //   'heatRecoveryType': heatRecoveryType,
  //   'formData.coolingRecoveryEfficiency': formData.coolingRecoveryEfficiency,
  //   'formData.heatingRecoveryEfficiency': formData.heatingRecoveryEfficiency,
  //   'coolingEfficiency': coolingEfficiency,
  //   'heatingEfficiency': heatingEfficiency
  // });

  row = worksheet.getRow(currentRow);
  row.getCell('A').value = heatRecoveryType;
  row.getCell('A').style = STYLES.TABLE_CELL_CENTER;

  // None인 경우 '-' 표시, 아니면 효율 값 표시
  if (isHeatRecoveryNone) {
    row.getCell('B').value = '-';
    row.getCell('B').style = STYLES.TABLE_CELL_CENTER;

    row.getCell('C').value = '-';
    row.getCell('C').style = STYLES.TABLE_CELL_CENTER;
  } else {
    row.getCell('B').value = coolingEfficiency;
    row.getCell('B').numFmt = '0.0';
    row.getCell('B').style = { ...STYLES.TABLE_CELL_CENTER, numFmt: '0.0' };

    row.getCell('C').value = heatingEfficiency;
    row.getCell('C').numFmt = '0.0';
    row.getCell('C').style = { ...STYLES.TABLE_CELL_CENTER, numFmt: '0.0' };
  }

  row.getCell('D').value = systemInfo.personnel;
  row.getCell('D').style = STYLES.TABLE_CELL_CENTER;

  row.getCell('E').value = personnelPerHour;
  row.getCell('E').numFmt = '0.0';
  row.getCell('E').style = { ...STYLES.TABLE_CELL_CENTER, numFmt: '0.0' };

  row.getCell('F').value = systemInfo.rshf;
  row.getCell('F').numFmt = '0.00';
  row.getCell('F').style = { ...STYLES.TABLE_CELL_CENTER, numFmt: '0.00' };

  row.getCell('G').value = systemInfo.area;
  row.getCell('G').numFmt = '#,##0.0';
  row.getCell('G').style = { ...STYLES.TABLE_CELL_HIGHLIGHT, numFmt: '#,##0.0', font: { ...STYLES.TABLE_CELL_HIGHLIGHT.font, bold: false }, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'center' } };

  row.getCell('H').value = systemInfo.ceilingHeight;
  row.getCell('H').numFmt = '0.0';
  row.getCell('H').style = { ...STYLES.TABLE_CELL_HIGHLIGHT, numFmt: '0.0', font: { ...STYLES.TABLE_CELL_HIGHLIGHT.font, bold: false }, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'center' } };

  row.getCell('I').value = systemInfo.volume;
  row.getCell('I').numFmt = '#,##0';
  row.getCell('I').style = { ...STYLES.TABLE_CELL_HIGHLIGHT, numFmt: '#,##0', font: { ...STYLES.TABLE_CELL_HIGHLIGHT.font, bold: false }, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'center' } };

  worksheet.mergeCells(`K${currentRow}:L${currentRow}`);
  row.getCell('K').value = 'Add & Hum';
  applyMergedCellStyle(worksheet, `K${currentRow}:L${currentRow}`, STYLES.TABLE_CELL_HIGHLIGHT);

  worksheet.mergeCells(`M${currentRow}:N${currentRow}`);
  row.getCell('M').value = parseFloat(loadPercentages.heating.addHum || '0');
  row.getCell('M').numFmt = '0.0';
  applyMergedCellStyle(worksheet, `M${currentRow}:N${currentRow}`, { ...STYLES.TABLE_CELL_HIGHLIGHT, numFmt: '0.0', font: { ...STYLES.TABLE_CELL_HIGHLIGHT.font, bold: false }, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'right' } });

  // J열 병합 (난방부하% 3행)
  worksheet.mergeCells(`J${currentRow - 2}:J${currentRow}`);
  applyMergedCellStyle(worksheet, `J${currentRow - 2}:J${currentRow}`, { ...STYLES.TABLE_CELL_HIGHLIGHT, alignment: { ...STYLES.TABLE_CELL_HIGHLIGHT.alignment, horizontal: 'center' } });
  currentRow++;

  currentRow++; // 빈 행

  // === □ 외기/실내 설계 조건 ===
  worksheet.mergeCells(`A${currentRow}:N${currentRow}`);
  const designCondCell = worksheet.getCell(`A${currentRow}`);
  designCondCell.value = '□ 외기/실내 설계 조건';
  designCondCell.style = {
    font: { name: '맑은 고딕', size: 11, bold: true },
    alignment: { vertical: 'middle', horizontal: 'left' }
  };
  worksheet.getRow(currentRow).height = 22;
  currentRow++;

  // Cooling 외기
  const coolingOutdoorBg = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE6E6' } } as ExcelJS.FillPattern;
  const coolingIndoorBg = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF5F5' } } as ExcelJS.FillPattern;

  row = worksheet.getRow(currentRow);
  row.getCell('A').value = 'Cooling';
  row.getCell('A').style = {
    ...STYLES.TABLE_CELL_BOLD,
    fill: coolingOutdoorBg,
    alignment: { ...STYLES.TABLE_CELL_BOLD.alignment, horizontal: 'center' }
  };

  row.getCell('B').value = '외기';
  row.getCell('B').style = {
    ...STYLES.TABLE_CELL_BOLD,
    fill: coolingIndoorBg,
    alignment: { ...STYLES.TABLE_CELL_BOLD.alignment, horizontal: 'center' }
  };

  // 개별 셀로 분리하여 오른쪽 정렬
  const coolingOutdoor = designConditions.cooling.outdoor;

  worksheet.mergeCells(`C${currentRow}:E${currentRow}`);
  row.getCell('C').value = `${coolingOutdoor.temp.toFixed(1)} ℃ DB`;
  applyMergedCellStyle(worksheet, `C${currentRow}:E${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`F${currentRow}:H${currentRow}`);
  row.getCell('F').value = `${coolingOutdoor.humidity.toFixed(1)} % RH`;
  applyMergedCellStyle(worksheet, `F${currentRow}:H${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`I${currentRow}:K${currentRow}`);
  row.getCell('I').value = `${coolingOutdoor.humidityRatio.toFixed(4)} kg/kg'`;
  applyMergedCellStyle(worksheet, `I${currentRow}:K${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`L${currentRow}:N${currentRow}`);
  row.getCell('L').value = `${coolingOutdoor.enthalpy.toFixed(2)} kJ/kg`;
  applyMergedCellStyle(worksheet, `L${currentRow}:N${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });
  currentRow++;

  // Cooling 실내
  row = worksheet.getRow(currentRow);
  row.getCell('B').value = '실내';
  row.getCell('B').style = {
    ...STYLES.TABLE_CELL_BOLD,
    fill: coolingIndoorBg,
    alignment: { ...STYLES.TABLE_CELL_BOLD.alignment, horizontal: 'center' }
  };

  // 개별 셀로 분리하여 오른쪽 정렬
  const coolingIndoor = designConditions.cooling.indoor;

  worksheet.mergeCells(`C${currentRow}:E${currentRow}`);
  row.getCell('C').value = `${coolingIndoor.temp.toFixed(1)} ℃ DB`;
  applyMergedCellStyle(worksheet, `C${currentRow}:E${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`F${currentRow}:H${currentRow}`);
  row.getCell('F').value = `${coolingIndoor.humidity.toFixed(1)} % RH`;
  applyMergedCellStyle(worksheet, `F${currentRow}:H${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`I${currentRow}:K${currentRow}`);
  row.getCell('I').value = `${coolingIndoor.humidityRatio.toFixed(4)} kg/kg'`;
  applyMergedCellStyle(worksheet, `I${currentRow}:K${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`L${currentRow}:N${currentRow}`);
  row.getCell('L').value = `${coolingIndoor.enthalpy.toFixed(2)} kJ/kg`;
  applyMergedCellStyle(worksheet, `L${currentRow}:N${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  // Cooling A열 병합
  worksheet.mergeCells(`A${currentRow - 1}:A${currentRow}`);
  applyMergedCellStyle(worksheet, `A${currentRow - 1}:A${currentRow}`, {
    ...STYLES.TABLE_CELL_BOLD,
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE6E6' } } as ExcelJS.FillPattern,
    alignment: { ...STYLES.TABLE_CELL_BOLD.alignment, horizontal: 'center' }
  });
  currentRow++;

  // Heating 외기
  const heatingOutdoorBg = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F2FF' } } as ExcelJS.FillPattern;
  const heatingIndoorBg = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F8FF' } } as ExcelJS.FillPattern;

  row = worksheet.getRow(currentRow);
  row.getCell('A').value = 'Heating';
  row.getCell('A').style = {
    ...STYLES.TABLE_CELL_BOLD,
    fill: heatingOutdoorBg,
    alignment: { ...STYLES.TABLE_CELL_BOLD.alignment, horizontal: 'center' }
  };

  row.getCell('B').value = '외기';
  row.getCell('B').style = {
    ...STYLES.TABLE_CELL_BOLD,
    fill: heatingIndoorBg,
    alignment: { ...STYLES.TABLE_CELL_BOLD.alignment, horizontal: 'center' }
  };

  // 개별 셀로 분리하여 오른쪽 정렬
  const heatingOutdoor = designConditions.heating.outdoor;

  worksheet.mergeCells(`C${currentRow}:E${currentRow}`);
  row.getCell('C').value = `${heatingOutdoor.temp.toFixed(1)} ℃ DB`;
  applyMergedCellStyle(worksheet, `C${currentRow}:E${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`F${currentRow}:H${currentRow}`);
  row.getCell('F').value = `${heatingOutdoor.humidity.toFixed(1)} % RH`;
  applyMergedCellStyle(worksheet, `F${currentRow}:H${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`I${currentRow}:K${currentRow}`);
  row.getCell('I').value = `${heatingOutdoor.humidityRatio.toFixed(4)} kg/kg'`;
  applyMergedCellStyle(worksheet, `I${currentRow}:K${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`L${currentRow}:N${currentRow}`);
  row.getCell('L').value = `${heatingOutdoor.enthalpy.toFixed(2)} kJ/kg`;
  applyMergedCellStyle(worksheet, `L${currentRow}:N${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });
  currentRow++;

  // Heating 실내
  row = worksheet.getRow(currentRow);
  row.getCell('B').value = '실내';
  row.getCell('B').style = {
    ...STYLES.TABLE_CELL_BOLD,
    fill: heatingIndoorBg,
    alignment: { ...STYLES.TABLE_CELL_BOLD.alignment, horizontal: 'center' }
  };

  // 개별 셀로 분리하여 오른쪽 정렬
  const heatingIndoor = designConditions.heating.indoor;

  worksheet.mergeCells(`C${currentRow}:E${currentRow}`);
  row.getCell('C').value = `${heatingIndoor.temp.toFixed(1)} ℃ DB`;
  applyMergedCellStyle(worksheet, `C${currentRow}:E${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`F${currentRow}:H${currentRow}`);
  row.getCell('F').value = `${heatingIndoor.humidity.toFixed(1)} % RH`;
  applyMergedCellStyle(worksheet, `F${currentRow}:H${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`I${currentRow}:K${currentRow}`);
  row.getCell('I').value = `${heatingIndoor.humidityRatio.toFixed(4)} kg/kg'`;
  applyMergedCellStyle(worksheet, `I${currentRow}:K${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`L${currentRow}:N${currentRow}`);
  row.getCell('L').value = `${heatingIndoor.enthalpy.toFixed(2)} kJ/kg`;
  applyMergedCellStyle(worksheet, `L${currentRow}:N${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  // Heating A열 병합
  worksheet.mergeCells(`A${currentRow - 1}:A${currentRow}`);
  applyMergedCellStyle(worksheet, `A${currentRow - 1}:A${currentRow}`, {
    ...STYLES.TABLE_CELL_BOLD,
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F2FF' } } as ExcelJS.FillPattern,
    alignment: { ...STYLES.TABLE_CELL_BOLD.alignment, horizontal: 'center' }
  });
  currentRow++;

  currentRow++; // 빈 행

  // === ■ AIR VOLUME SELECTION ===
  worksheet.mergeCells(`A${currentRow}:N${currentRow}`);
  const airVolumeCell = worksheet.getCell(`A${currentRow}`);
  airVolumeCell.value = '■ AIR VOLUME SELECTION';
  airVolumeCell.style = STYLES.SECTION_HEADER;
  worksheet.getRow(currentRow).height = 22;
  currentRow++;

  // 급기량
  const supplyAirSelectionMethod = formData.supplyAirSelectionCriteria || 'standard';
  const supplyTempDelta = parseFloat(formData.supplyTempDelta) || 10;
  let supplyCalc = '';

  if (supplyAirSelectionMethod === 'direct') {
    supplyCalc = `직접 입력: ${Math.round(systemInfo.supplyAirflow).toLocaleString()} ㎥/h`;
  } else {
    supplyCalc = `${systemInfo.sensibleCoolingLoad.toLocaleString()} W ÷ (0.335 × ${supplyTempDelta} ℃) = ${Math.round(systemInfo.supplyAirflow).toLocaleString()} ㎥/h`;
  }

  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
  row.getCell('A').value = 'Supply Air Volume';
  applyMergedCellStyle(worksheet, `A${currentRow}:C${currentRow}`, STYLES.TABLE_CELL_BOLD);

  // D-E열: 값과 단위 (오른쪽 정렬)
  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = `${Math.round(systemInfo.supplyAirflow).toLocaleString()} ㎥/h`;
  applyMergedCellStyle(worksheet, `D${currentRow}:E${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  // F-N열: 수식 영역 (온도 영역 오른쪽 경계선과 정렬)
  worksheet.mergeCells(`F${currentRow}:N${currentRow}`);
  row.getCell('F').value = supplyCalc;
  applyMergedCellStyle(worksheet, `F${currentRow}:N${currentRow}`, STYLES.TABLE_CELL);
  currentRow++;

  // 외기량
  const outdoorAirSelectionMethod = formData.outdoorAirSelectionCriteria || 'standard';
  let outdoorAirCalculation = '';

  if (outdoorAirSelectionMethod === 'occupancy') {
    outdoorAirCalculation = `${systemInfo.personnel} p × ${personnelPerHour} ㎥/h/p = ${Math.round(systemInfo.personnel * personnelPerHour).toLocaleString()} ㎥/h`;
  } else if (outdoorAirSelectionMethod === 'direct') {
    outdoorAirCalculation = `직접 입력: ${Math.round(systemInfo.outdoorAirflow).toLocaleString()} ㎥/h`;
  } else {
    outdoorAirCalculation = `급기량과 동일: ${Math.round(systemInfo.outdoorAirflow).toLocaleString()} ㎥/h`;
  }

  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
  row.getCell('A').value = 'Outdoor Air Volume';
  applyMergedCellStyle(worksheet, `A${currentRow}:C${currentRow}`, STYLES.TABLE_CELL_BOLD);

  // D-E열: 값과 단위 (오른쪽 정렬)
  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = `${Math.round(systemInfo.outdoorAirflow).toLocaleString()} ㎥/h`;
  applyMergedCellStyle(worksheet, `D${currentRow}:E${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  // F-N열: 수식 영역
  worksheet.mergeCells(`F${currentRow}:N${currentRow}`);
  row.getCell('F').value = outdoorAirCalculation;
  applyMergedCellStyle(worksheet, `F${currentRow}:N${currentRow}`, STYLES.TABLE_CELL);
  currentRow++;

  // 배기량
  const exhaustAirflowVal = parseFloat(formData.exhaustAirflow) || 0;
  const pressurizationAirflow = parseFloat(formData.pressurizationAirflow) || 0;
  const exhaustCalc = `배기량(${Math.round(exhaustAirflowVal).toLocaleString()}) + 가압량(${Math.round(pressurizationAirflow).toLocaleString()})`;

  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
  row.getCell('A').value = 'Exhaust Air Volume';
  applyMergedCellStyle(worksheet, `A${currentRow}:C${currentRow}`, STYLES.TABLE_CELL_BOLD);

  // D-E열: 값과 단위 (오른쪽 정렬)
  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = `${Math.round(systemInfo.exhaustAirflow).toLocaleString()} ㎥/h`;
  applyMergedCellStyle(worksheet, `D${currentRow}:E${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  // F-N열: 수식 영역
  worksheet.mergeCells(`F${currentRow}:N${currentRow}`);
  row.getCell('F').value = exhaustCalc;
  applyMergedCellStyle(worksheet, `F${currentRow}:N${currentRow}`, STYLES.TABLE_CELL);
  currentRow++;

  currentRow++; // 빈 행

  // === ■ COOLING / HEATING COIL & HUMIDIFIER CAPACITY ===
  worksheet.mergeCells(`A${currentRow}:N${currentRow}`);
  const coilCapCell = worksheet.getCell(`A${currentRow}`);
  coilCapCell.value = '■ COOLING / HEATING COIL & HUMIDIFIER CAPACITY';
  coilCapCell.style = STYLES.SECTION_HEADER;
  worksheet.getRow(currentRow).height = 22;
  currentRow++;

  // 냉방용 헤더 (2행 1열 병합)
  const humidifierTypeValue = formData.humidifierType || 'None';
  const coolingStartRow = currentRow;

  // 냉방
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`B${currentRow}:C${currentRow}`);
  row.getCell('B').value = '냉방';
  applyMergedCellStyle(worksheet, `B${currentRow}:C${currentRow}`, STYLES.TABLE_CELL_BOLD);

  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = `${Math.round(coilCapacities.cooling.capacity).toLocaleString()} W`;
  applyMergedCellStyle(worksheet, `D${currentRow}:E${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`F${currentRow}:N${currentRow}`);
  row.getCell('F').value = formatFormulaNumbers(coilCapacities.cooling.formula) || '습공기선도 데이터 없음';
  applyMergedCellStyle(worksheet, `F${currentRow}:N${currentRow}`, STYLES.TABLE_CELL);
  currentRow++;

  // 재열
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`B${currentRow}:C${currentRow}`);
  row.getCell('B').value = '재열';
  applyMergedCellStyle(worksheet, `B${currentRow}:C${currentRow}`, STYLES.TABLE_CELL_BOLD);

  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = `${Math.round(coilCapacities.reheating.capacity).toLocaleString()} W`;
  applyMergedCellStyle(worksheet, `D${currentRow}:E${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`F${currentRow}:N${currentRow}`);
  row.getCell('F').value = formatFormulaNumbers(coilCapacities.reheating.formula) || '';
  applyMergedCellStyle(worksheet, `F${currentRow}:N${currentRow}`, STYLES.TABLE_CELL);
  currentRow++;

  // 냉방용 A열 병합 (2행)
  worksheet.mergeCells(`A${coolingStartRow}:A${currentRow - 1}`);
  applyMergedCellStyle(worksheet, `A${coolingStartRow}:A${currentRow - 1}`, { ...STYLES.TABLE_CELL_BOLD, alignment: { ...STYLES.TABLE_CELL_BOLD.alignment, horizontal: 'center' } });
  worksheet.getCell(`A${coolingStartRow}`).value = '냉방용';

  // 난방용 헤더 (3행 1열 병합)
  const heatingStartRow = currentRow;

  // 예열
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`B${currentRow}:C${currentRow}`);
  row.getCell('B').value = '예열';
  applyMergedCellStyle(worksheet, `B${currentRow}:C${currentRow}`, STYLES.TABLE_CELL_BOLD);

  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = `${Math.round(coilCapacities.preheating.capacity).toLocaleString()} W`;
  applyMergedCellStyle(worksheet, `D${currentRow}:E${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`F${currentRow}:N${currentRow}`);
  row.getCell('F').value = formatFormulaNumbers(coilCapacities.preheating.formula) || '';
  applyMergedCellStyle(worksheet, `F${currentRow}:N${currentRow}`, STYLES.TABLE_CELL);
  currentRow++;

  // 가열
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`B${currentRow}:C${currentRow}`);
  row.getCell('B').value = '가열';
  applyMergedCellStyle(worksheet, `B${currentRow}:C${currentRow}`, STYLES.TABLE_CELL_BOLD);

  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = `${Math.round(coilCapacities.heating.capacity).toLocaleString()} W`;
  applyMergedCellStyle(worksheet, `D${currentRow}:E${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`F${currentRow}:N${currentRow}`);
  row.getCell('F').value = formatFormulaNumbers(coilCapacities.heating.formula) || '습공기선도 데이터 없음';
  applyMergedCellStyle(worksheet, `F${currentRow}:N${currentRow}`, STYLES.TABLE_CELL);
  currentRow++;

  // 가습
  row = worksheet.getRow(currentRow);
  worksheet.mergeCells(`B${currentRow}:C${currentRow}`);
  row.getCell('B').value = '가습';
  applyMergedCellStyle(worksheet, `B${currentRow}:C${currentRow}`, STYLES.TABLE_CELL_BOLD);

  worksheet.mergeCells(`D${currentRow}:E${currentRow}`);
  row.getCell('D').value = `${coilCapacities.humidifier.capacity.toFixed(2)} kg/h`;
  applyMergedCellStyle(worksheet, `D${currentRow}:E${currentRow}`, { ...STYLES.TABLE_CELL, alignment: { ...STYLES.TABLE_CELL.alignment, horizontal: 'right' } });

  worksheet.mergeCells(`F${currentRow}:N${currentRow}`);
  row.getCell('F').value = formatFormulaNumbers(coilCapacities.humidifier.formula) || '';
  applyMergedCellStyle(worksheet, `F${currentRow}:N${currentRow}`, STYLES.TABLE_CELL);
  currentRow++;

  // 난방용 A열 병합 (3행)
  worksheet.mergeCells(`A${heatingStartRow}:A${currentRow - 1}`);
  applyMergedCellStyle(worksheet, `A${heatingStartRow}:A${currentRow - 1}`, { ...STYLES.TABLE_CELL_BOLD, alignment: { ...STYLES.TABLE_CELL_BOLD.alignment, horizontal: 'center' } });
  worksheet.getCell(`A${heatingStartRow}`).value = '난방용';

  currentRow++; // 빈 행

  // === □ 코일 형식 / 가습기 제원 ===
  worksheet.mergeCells(`A${currentRow}:N${currentRow}`);
  const coilSpecCell = worksheet.getCell(`A${currentRow}`);
  coilSpecCell.value = '□ 코일 형식 / 가습기 제원';
  coilSpecCell.style = {
    font: { name: '맑은 고딕', size: 11, bold: true },
    alignment: { vertical: 'middle', horizontal: 'left' }
  };
  worksheet.getRow(currentRow).height = 22;
  currentRow++;

  // 코일 데이터
  const coolingCoilType = formData.coolingCoilType || 'Water';
  const coolingWaterInTemp = parseFloat(formData.coolingWaterInTemp) || 7;
  const coolingWaterOutTemp = parseFloat(formData.coolingWaterOutTemp) || 12;
  const coolingWaterFlow = Math.round(coilCapacities.cooling.capacity / (4.2 * (coolingWaterOutTemp - coolingWaterInTemp) * 60)) || 0;

  const heatingCoilType = formData.heatingCoilType || 'Water';
  const heatingWaterInTemp = parseFloat(formData.heatingWaterInTemp) || 80;
  const heatingWaterOutTemp = parseFloat(formData.heatingWaterOutTemp) || 70;
  const heatingWaterFlow = Math.round(coilCapacities.heating.capacity / (4.2 * (heatingWaterInTemp - heatingWaterOutTemp) * 60)) || 0;

  // Cool'g Coil, P/H Coil
  row = worksheet.getRow(currentRow);
  row.getCell('A').value = "Cool'g Coil";
  row.getCell('A').style = { ...STYLES.TABLE_CELL_BOLD, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } } as ExcelJS.FillPattern };

  worksheet.mergeCells(`B${currentRow}:E${currentRow}`);
  row.getCell('B').value = `${coolingCoilType} (${coolingWaterInTemp}/${coolingWaterOutTemp} ℃, ${coolingWaterFlow} lpm)`;
  applyMergedCellStyle(worksheet, `B${currentRow}:E${currentRow}`, STYLES.TABLE_CELL);

  worksheet.mergeCells(`F${currentRow}:G${currentRow}`);
  row.getCell('F').value = 'P/H Coil';
  applyMergedCellStyle(worksheet, `F${currentRow}:G${currentRow}`, { ...STYLES.TABLE_CELL_BOLD, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } } as ExcelJS.FillPattern });

  worksheet.mergeCells(`H${currentRow}:N${currentRow}`);
  row.getCell('H').value = formData.preheatingCoilType || 'None';
  applyMergedCellStyle(worksheet, `H${currentRow}:N${currentRow}`, STYLES.TABLE_CELL);
  currentRow++;

  // R/H Coil, A/H Coil
  row = worksheet.getRow(currentRow);
  row.getCell('A').value = 'R/H Coil';
  row.getCell('A').style = { ...STYLES.TABLE_CELL_BOLD, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } } as ExcelJS.FillPattern };

  worksheet.mergeCells(`B${currentRow}:E${currentRow}`);
  row.getCell('B').value = formData.reheatingCoilType || 'None';
  applyMergedCellStyle(worksheet, `B${currentRow}:E${currentRow}`, STYLES.TABLE_CELL);

  worksheet.mergeCells(`F${currentRow}:G${currentRow}`);
  row.getCell('F').value = 'A/H Coil';
  applyMergedCellStyle(worksheet, `F${currentRow}:G${currentRow}`, { ...STYLES.TABLE_CELL_BOLD, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } } as ExcelJS.FillPattern });

  worksheet.mergeCells(`H${currentRow}:N${currentRow}`);
  row.getCell('H').value = `${heatingCoilType} (${heatingWaterInTemp}/${heatingWaterOutTemp} ℃, ${heatingWaterFlow} lpm)`;
  applyMergedCellStyle(worksheet, `H${currentRow}:N${currentRow}`, STYLES.TABLE_CELL);
  currentRow++;

  // 가습기 형식 (A33)
  row = worksheet.getRow(currentRow);
  row.getCell('A').value = '가습기 형식';
  row.getCell('A').style = { ...STYLES.TABLE_CELL_BOLD, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } } as ExcelJS.FillPattern };

  worksheet.mergeCells(`B${currentRow}:N${currentRow}`);
  row.getCell('B').value = humidifierTypeValue;
  applyMergedCellStyle(worksheet, `B${currentRow}:N${currentRow}`, STYLES.TABLE_CELL);

  // A33 행에 페이지 나누기 추가 (A33 이후부터 새 페이지)
  worksheet.getRow(currentRow).addPageBreak();
  currentRow++; // A34로 이동 (새 페이지 시작)

  // 습공기선도 제목
  const chartTitleRow = currentRow;
  worksheet.mergeCells(`A${chartTitleRow}:N${chartTitleRow}`);
  const chartTitleCell = worksheet.getCell(`A${chartTitleRow}`);
  chartTitleCell.value = `■ PSYCHROMETRIC CHART (습공기선도) - ${ahu.ahuNo}`;
  chartTitleCell.style = STYLES.SUBTITLE;
  currentRow++;

  // 빈 행 추가
  currentRow++;

  // 습공기선도 이미지 및 상태점 정보 삽입
  if (ahu.psychroChartImage && ahu.psychroChartData?.points) {
    try {
      const points = ahu.psychroChartData.points;

      // 냉방/난방 상태점 분리
      const heatingPoints = points.filter((p: any) => {
        const name = p.name.toLowerCase();
        return name === 'o2' || name === 'r2' || name === 'm1' || name === 'l2' || name === 'l3';
      });
      const coolingPoints = points.filter((p: any) => {
        const name = p.name.toLowerCase();
        return !(name === 'o2' || name === 'r2' || name === 'm1' || name === 'l2' || name === 'l3');
      });

      const statePointsStartRow = currentRow;

      // 냉방 상태점 테이블 (A-C 열) - 엔탈피 열 추가
      if (coolingPoints.length > 0) {
        // 냉방 상태점 헤더
        worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
        const coolingHeaderCell = worksheet.getCell(`A${currentRow}`);
        coolingHeaderCell.value = '■ 냉방 상태점';
        coolingHeaderCell.style = {
          font: { name: '맑은 고딕', size: 10, bold: true, color: { argb: 'FF2196F3' } },
          alignment: { vertical: 'middle', horizontal: 'left' },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } } as ExcelJS.FillPattern
        };
        currentRow++;

        // 테이블 헤더 (엔탈피 열 추가)
        row = worksheet.getRow(currentRow);
        row.getCell('A').value = '상태점';
        row.getCell('B').value = '건구온도(°C)';
        row.getCell('C').value = '상대습도(%)';
        row.getCell('D').value = '엔탈피(kJ/kg)';
        ['A', 'B', 'C', 'D'].forEach(col => {
          const cell = row.getCell(col);
          cell.style = {
            font: { name: '맑은 고딕', size: 9, bold: true },
            alignment: { vertical: 'middle', horizontal: 'center' },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } } as ExcelJS.FillPattern,
            border: {
              top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
            }
          };
        });
        currentRow++;

        // 상태점 데이터
        coolingPoints.forEach((point: any) => {
          row = worksheet.getRow(currentRow);
          const displayName = getStatePointDisplayName(point);

          // 값 검증 및 안전한 변환
          const dryBulbTemp = typeof point.dryBulbTemp === 'number' && !isNaN(point.dryBulbTemp)
            ? point.dryBulbTemp.toFixed(1)
            : '-';
          const relativeHumidity = typeof point.relativeHumidity === 'number' && !isNaN(point.relativeHumidity)
            ? point.relativeHumidity.toFixed(1)
            : '-';
          const enthalpy = typeof point.enthalpy === 'number' && !isNaN(point.enthalpy)
            ? point.enthalpy.toFixed(2)
            : '-';

          row.getCell('A').value = sanitizeCellValue(displayName);
          row.getCell('B').value = sanitizeCellValue(dryBulbTemp !== '-' ? dryBulbTemp : '-');
          row.getCell('C').value = sanitizeCellValue(relativeHumidity !== '-' ? relativeHumidity : '-');
          row.getCell('D').value = sanitizeCellValue(enthalpy !== '-' ? enthalpy : '-');

          ['A', 'B', 'C', 'D'].forEach(col => {
            const cell = row.getCell(col);
            cell.style = {
              font: { name: '맑은 고딕', size: 9 },
              alignment: { vertical: 'middle', horizontal: col === 'A' ? 'left' : 'right' },
              border: {
                top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
              }
            };
          });
          currentRow++;
        });

        currentRow++; // 냉방/난방 사이 공백
      }

      // 난방 상태점 테이블 (A-D 열) - 엔탈피 열 추가
      if (heatingPoints.length > 0) {
        worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
        const heatingHeaderCell = worksheet.getCell(`A${currentRow}`);
        heatingHeaderCell.value = '■ 난방 상태점';
        heatingHeaderCell.style = {
          font: { name: '맑은 고딕', size: 10, bold: true, color: { argb: 'FFF44336' } },
          alignment: { vertical: 'middle', horizontal: 'left' },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } } as ExcelJS.FillPattern
        };
        currentRow++;

        // 테이블 헤더 (엔탈피 열 추가)
        row = worksheet.getRow(currentRow);
        row.getCell('A').value = '상태점';
        row.getCell('B').value = '건구온도(°C)';
        row.getCell('C').value = '상대습도(%)';
        row.getCell('D').value = '엔탈피(kJ/kg)';
        ['A', 'B', 'C', 'D'].forEach(col => {
          const cell = row.getCell(col);
          cell.style = {
            font: { name: '맑은 고딕', size: 9, bold: true },
            alignment: { vertical: 'middle', horizontal: 'center' },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } } as ExcelJS.FillPattern,
            border: {
              top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
              right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
            }
          };
        });
        currentRow++;

        // 상태점 데이터
        heatingPoints.forEach((point: any) => {
          row = worksheet.getRow(currentRow);
          const displayName = getStatePointDisplayName(point);

          // 값 검증 및 안전한 변환
          const dryBulbTemp = typeof point.dryBulbTemp === 'number' && !isNaN(point.dryBulbTemp)
            ? point.dryBulbTemp.toFixed(1)
            : '-';
          const relativeHumidity = typeof point.relativeHumidity === 'number' && !isNaN(point.relativeHumidity)
            ? point.relativeHumidity.toFixed(1)
            : '-';
          const enthalpy = typeof point.enthalpy === 'number' && !isNaN(point.enthalpy)
            ? point.enthalpy.toFixed(2)
            : '-';

          row.getCell('A').value = sanitizeCellValue(displayName);
          row.getCell('B').value = sanitizeCellValue(dryBulbTemp !== '-' ? dryBulbTemp : '-');
          row.getCell('C').value = sanitizeCellValue(relativeHumidity !== '-' ? relativeHumidity : '-');
          row.getCell('D').value = sanitizeCellValue(enthalpy !== '-' ? enthalpy : '-');

          ['A', 'B', 'C', 'D'].forEach(col => {
            const cell = row.getCell(col);
            cell.style = {
              font: { name: '맑은 고딕', size: 9 },
              alignment: { vertical: 'middle', horizontal: col === 'A' ? 'left' : 'right' },
              border: {
                top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
              }
            };
          });
          currentRow++;
        });
      }

      // 습공기선도 이미지 삽입 (임시로 비활성화 - drawing XML 오류 방지)
      // TODO: ExcelJS 이미지 삽입 문제 해결 후 다시 활성화
      //console.log(`ℹ️ 이미지 삽입 임시 비활성화 (${ahu.ahuNo || ahu.id})`);

      // 상태점 테이블의 마지막 행 계산 (currentRow는 이미 다음 행을 가리킴)
      const imageEndRow = currentRow - 1;
      const imageRowCount = imageEndRow - statePointsStartRow + 1;
      const minImageRows = 10; // 최소 이미지 영역 높이
      const actualImageEndRow = imageEndRow < statePointsStartRow + minImageRows
        ? statePointsStartRow + minImageRows
        : imageEndRow;

      // 습공기선도 이미지 삽입 (E열부터 N열)
      try {
        const imageBase64 = ahu.psychroChartImage.split(',')[1];
        const imageId = workbook.addImage({
          base64: imageBase64,
          extension: 'png',
        });

        // 이미지 삽입 위치 계산 - 오른쪽 하단이 N61 셀에 위치하도록 설정
        // N열은 13번째 열(col=13), 61행은 row=60(0-based)
        // 적절한 크기로 역산하여 시작 위치 조정
        worksheet.addImage(imageId, {
          tl: { col: 4, row: statePointsStartRow - 1 }, // E열 (0-based: col=4) 시작
          br: { col: 14, row: 63 }, // N61 셀(0-based: col=13, row=60)까지
          editAs: 'oneCell'
        });

        //console.log(`✅ 습공기선도 이미지 삽입 완료: ${ahu.ahuNo || ahu.id}`);
      } catch (imageError) {
        //console.error(`❌ 이미지 삽입 실패 (${ahu.ahuNo || ahu.id}):`, imageError);
        // 이미지 삽입 실패 시 플레이스홀더 표시
        worksheet.mergeCells(`E${statePointsStartRow}:N${actualImageEndRow}`);
        const placeholderCell = worksheet.getCell(`E${statePointsStartRow}`);
        placeholderCell.value = '[ 이미지 삽입 실패 ]';
        placeholderCell.style = {
          ...STYLES.TABLE_CELL,
          alignment: { vertical: 'middle', horizontal: 'center', wrapText: true },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF0F0' } } as ExcelJS.FillPattern,
          font: { name: '맑은 고딕', size: 10, italic: true, color: { argb: 'FFFF0000' } }
        };
      }

      // currentRow 업데이트 (이미지 영역이 상태점 테이블보다 길 경우)
      if (actualImageEndRow >= currentRow) {
        currentRow = actualImageEndRow + 1;
      }

      //console.log(`✅ 습공기선도 섹션 완료: ${ahu.ahuNo || ahu.id}`);
    } catch (error) {
      //console.error(`❌ 습공기선도 처리 실패 (${ahu.ahuNo || ahu.id}):`, error);
      const noChartCell = worksheet.getCell(`A${currentRow}`);
      noChartCell.value = '습공기선도 차트를 생성할 수 없습니다.';
      noChartCell.style = STYLES.TABLE_CELL;
      currentRow++;
    }
  } else {
    console.warn(`⚠️ 습공기선도 데이터가 없음: ${ahu.ahuNo || ahu.id}`);
    const noChartCell = worksheet.getCell(`A${currentRow}`);
    noChartCell.value = '습공기선도 차트를 생성할 수 없습니다. (출력 시트를 다시 생성해 주세요)';
    noChartCell.style = STYLES.TABLE_CELL;
    currentRow++;
  }

  // FAN SELECTION 섹션을 A64부터 시작
  currentRow = 64;

  // 페이지 분할을 A64(63행 다음)에 추가
  worksheet.getRow(63).addPageBreak();

  // ============================================
  // FAN SELECTION 섹션 (완전한 버전)
  // ============================================
  currentRow = addFanSelection(worksheet, ahu, currentRow, STYLES);

  // ============================================
  // ENTERING/LEAVING AIR STATUS 섹션 (완전한 버전)
  // ============================================
  currentRow = addEnteringLeavingAirStatus(worksheet, ahu, currentRow, STYLES);

  return worksheet;
};

/**
 * 습공기선도 시트 생성
 */
const createPsychrometricChartSheet = async (
  workbook: ExcelJS.Workbook,
  ahu: any,
  index: number
): Promise<void> => {
  const sheetName = `${ahu.ahuNo || `AHU-${index + 1}`}-Chart`;
  const worksheet = workbook.addWorksheet(sheetName, {
    pageSetup: {
      paperSize: 9, // A4
      orientation: 'landscape',
      margins: { left: 0.5, right: 0.5, top: 0.7, bottom: 0.7, header: 0.3, footer: 0.3 },
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 1
    }
  });

  let currentRow = 1;

  // 제목
  worksheet.mergeCells(`A${currentRow}:L${currentRow}`);
  const titleCell = worksheet.getCell(`A${currentRow}`);
  titleCell.value = `■ PSYCHROMETRIC CHART (습공기선도) - ${ahu.ahuNo}`;
  titleCell.style = STYLES.SECTION_HEADER;
  worksheet.getRow(currentRow).height = 22;
  currentRow++;

  // 저장된 습공기선도 이미지 사용
  const chartImage = ahu.psychroChartImage;

  if (chartImage && chartImage.startsWith('data:image/png;base64,')) {
    try {
      const base64Data = chartImage.replace(/^data:image\/png;base64,/, '');

      // base64 데이터 검증
      if (base64Data && base64Data.length > 100) {
        const imageId = workbook.addImage({
          buffer: base64ToArrayBuffer(base64Data),
          extension: 'png'
        });

        // 이미지 삽입 (A4 가로 방향 전체 사용)
        worksheet.addImage(imageId, {
          tl: { col: 0, row: currentRow },
          ext: { width: 950, height: 600 }
        });

        // 이미지를 위한 행 높이 설정
        for (let i = 0; i < 45; i++) {
          worksheet.getRow(currentRow + i).height = 13.5;
        }

        console.log(`✅ 습공기선도 차트 이미지 사용: ${ahu.ahuNo || ahu.id}`);
      } else {
        throw new Error('Invalid base64 data length');
      }
    } catch (error) {
      console.error(`❌ 습공기선도 이미지 처리 실패 (${ahu.ahuNo || ahu.id}):`, error);
      worksheet.mergeCells(`A${currentRow}:L${currentRow}`);
      const noChartCell = worksheet.getCell(`A${currentRow}`);
      noChartCell.value = '습공기선도 차트를 생성할 수 없습니다.';
      noChartCell.style = {
        font: { name: '맑은 고딕', size: 12, color: { argb: 'FFFF0000' } },
        alignment: { vertical: 'middle', horizontal: 'center' }
      };
    }
  } else {
    console.warn(`⚠️ 습공기선도 차트 이미지가 저장되지 않았습니다: ${ahu.ahuNo || ahu.id}`);
    worksheet.mergeCells(`A${currentRow}:L${currentRow}`);
    const noChartCell = worksheet.getCell(`A${currentRow}`);
    noChartCell.value = '습공기선도 차트를 생성할 수 없습니다. (출력 시트를 다시 생성해 주세요)';
    noChartCell.style = {
      font: { name: '맑은 고딕', size: 12, color: { argb: 'FFFF0000' } },
      alignment: { vertical: 'middle', horizontal: 'center' }
    };
  }
};

/**
 * AHU 선정 Excel 파일 생성 및 다운로드
 */
export const exportAHSelectionToExcel = async (
  ahuData: any[],
  projectName: string = '프로젝트'
): Promise<boolean> => {
  try {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'RTS-SAREK';
    workbook.created = new Date();
    workbook.lastModifiedBy = 'RTS-SAREK';

    // 각 공조기별로 상세 시트 생성 (습공기선도 포함)
    for (let index = 0; index < ahuData.length; index++) {
      const ahu = ahuData[index];

      if (!ahu.outputSheetData) {
        console.warn(`⚠️ AHU ${ahu.ahuNo}: outputSheetData가 없어 스킵합니다.`);
        continue;
      }

      // AHU 상세 시트 생성 (A33 아래에 습공기선도 포함)
      await createAHUDetailSheet(workbook, ahu, index);
    }

    // 파일 저장
    const buffer = await workbook.xlsx.writeBuffer();
    const today = new Date();
    const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
    const fileName = `${projectName}_AHU_Selection_${dateStr}.xlsx`;
    saveAs(new Blob([buffer]), fileName);

    return true;
  } catch (error) {
    console.error('❌ Excel export 실패:', error);
    console.error('에러 스택:', error instanceof Error ? error.stack : 'No stack trace');
    console.error('에러 메시지:', error instanceof Error ? error.message : String(error));
    throw error;
  }
};
